import React from 'react';
import useAuthStore from '../store/authStore';
import useUserOnlineStatus from '../hooks/useUserOnlineStatus';
import { useDarkMode } from '../contexts/DarkModeContext';
import { getImageUrl } from '../utils/api';

export default function ProductModal({ conversation, isOpen, onClose }) {
  const { user } = useAuthStore();
  const { isDarkMode } = useDarkMode();
  const currentUserEmail = user?.email;
  const currentUserUid = user?.uid;

  // Debug log to ensure this component is being rendered
  console.log('ProductModal rendered:', { isOpen, conversation: conversation?.product_name });

  // Use product details from the conversation object, fallback to sample if missing
  const product = {
    name: conversation?.product_name || 'Product',
    price: conversation?.product_price || 'N/A',
    status: conversation?.product_status || 'Available',
    image: conversation?.product_image ? getImageUrl(conversation.product_image) : 'https://img.icons8.com/color/96/000000/youtube-play.png',
    features: conversation?.product_features || ['Monetized', 'Verified', 'Fast Delivery'],
    description: conversation?.product_description || '',
    category: conversation?.product_category || '',
    seller: {
      name: conversation?.seller?.name || conversation?.seller_name || 'Seller',
      avatar: conversation?.seller?.avatar || conversation?.seller_avatar || 'https://i.pravatar.cc/40?img=1',
      rating: conversation?.seller?.rating || 4.9,
      reviews: conversation?.seller?.reviews || 128,
      verified: typeof conversation?.seller?.verified !== 'undefined' ? conversation.seller.verified : true,
      email: conversation?.seller?.email || '',
      uid: conversation?.seller?.uid || '',
      username: conversation?.seller?.username || '',
    },
    buyer: {
      name: conversation?.buyer?.name || conversation?.buyer_name || 'Buyer',
      avatar: conversation?.buyer?.avatar || conversation?.buyer_avatar || 'https://i.pravatar.cc/40?img=2',
      rating: conversation?.buyer?.rating || 4.9,
      reviews: conversation?.buyer?.reviews || 128,
      verified: typeof conversation?.buyer?.verified !== 'undefined' ? conversation.buyer.verified : true,
      email: conversation?.buyer?.email || '',
      uid: conversation?.buyer?.uid || '',
      username: conversation?.buyer?.username || '',
    },
    product_id: conversation?.product_id || '',
    url: conversation?.product_id ? `/product/${conversation.product_id}` : '#',
  };

  // Determine which party to show (other than current user)
  let showSeller = true;
  if (
    (product.seller.uid && currentUserUid && product.seller.uid === currentUserUid) ||
    (!product.seller.uid && product.seller.email && currentUserEmail && product.seller.email === currentUserEmail)
  ) {
    showSeller = false;
  }

  let cardTitle = showSeller ? 'Seller' : 'Buyer';
  let cardName = showSeller ? (product.seller.name || 'Seller') : (product.buyer.name || 'Buyer');
  let cardAvatar = showSeller ? (product.seller.avatar) : (product.buyer.avatar);
  
  if (!cardAvatar || cardAvatar.trim() === '' || cardAvatar.includes('pravatar.cc')) {
    cardAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(cardName)}&background=dddddd&color=222222&rounded=true&size=64`;
  }
  
  let cardUsername = showSeller ? (product.seller.username || '') : (product.buyer.username || '');
  let cardEmail = showSeller ? (product.seller.email || '') : (product.buyer.email || '');
  let cardUid = showSeller ? (product.seller.uid || '') : (product.buyer.uid || '');
  let profileUrl = showSeller
    ? (conversation?.seller_username ? `/profile/${conversation.seller_username}` : '#')
    : (conversation?.buyer_username ? `/profile/${conversation.buyer_username}` : '#');
  
  const cardOnline = useUserOnlineStatus(cardUid);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black bg-opacity-50" onClick={onClose}>
      {/* Modal Container - Responsive sizing */}
      <div className={`${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'} rounded-3xl shadow-2xl w-full max-w-[95vw] sm:max-w-md md:max-w-lg lg:max-w-2xl max-h-[90vh] overflow-y-auto mx-2`} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 sm:p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
          <h2 className={`text-lg sm:text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Product Details</h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-full transition-colors ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
            aria-label="Close modal"
          >
            <svg className={`w-5 h-5 sm:w-6 sm:h-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Product Card */}
          <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gradient-to-br from-gray-50 to-white border-gray-100'} rounded-2xl shadow-lg border overflow-hidden`}>
            {/* Product Image */}
            <div className={`relative h-40 sm:h-48 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} flex items-center justify-center`}>
              <a href={product.url} target="_blank" rel="noopener noreferrer">
                <img 
                  src={product.image} 
                  alt="Product" 
                  className="w-full h-full object-cover"
                />
              </a>
              <span className="absolute top-2 sm:top-4 left-2 sm:left-4 bg-green-500 text-white text-xs px-2 sm:px-3 py-1 rounded-full shadow font-semibold">
                {product.status}
              </span>
              <span className="absolute top-2 sm:top-4 right-2 sm:right-4 bg-red-500 text-white text-xs px-2 sm:px-3 py-1 rounded-full shadow font-semibold">
                ${product.price}
              </span>
            </div>
            
            {/* Product Info */}
            <div className="p-4 sm:p-6">
              <a href={product.url} target="_blank" rel="noopener noreferrer">
                <h3 className={`font-bold text-lg mb-2 hover:underline cursor-pointer ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {product.name}
                </h3>
              </a>
              <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{product.description}</p>
              <p className={`text-xs mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>{product.category}</p>
              
              {/* View Product Button */}
              <a href={product.url} target="_blank" rel="noopener noreferrer">
                <button className="w-full bg-red-500 hover:bg-red-600 text-white font-bold py-3 rounded-xl shadow-lg transition duration-200">
                  View Product
                </button>
              </a>
            </div>
          </div>

          {/* User Details Card */}
          <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gradient-to-br from-gray-50 to-white border-gray-100'} rounded-2xl shadow-lg border p-4 sm:p-6`}>
            <h3 className={`font-bold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{cardTitle} Information</h3>
            
            <div className="flex items-center gap-3 sm:gap-4">
              {/* Avatar */}
              <div className="relative">
                {profileUrl !== '#' ? (
                  <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="group">
                    <img
                      src={cardAvatar}
                      alt={cardName}
                      className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full ${isDarkMode ? 'bg-gray-600 border-gray-600' : 'bg-gray-200 border-white'} border-4 shadow group-hover:opacity-90`}
                    />
                  </a>
                ) : (
                  <img
                    src={cardAvatar}
                    alt={cardName}
                    className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full ${isDarkMode ? 'bg-gray-600 border-gray-600' : 'bg-gray-200 border-white'} border-4 shadow`}
                  />
                )}
                <span className={`absolute bottom-1 right-1 w-4 h-4 rounded-full border-2 ${isDarkMode ? 'border-gray-800' : 'border-white'} ${cardOnline ? 'bg-green-500' : 'bg-gray-400'}`}></span>
              </div>

              {/* User Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  {profileUrl !== '#' ? (
                    <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">
                      <span className={`font-semibold text-base ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{cardName}</span>
                    </a>
                  ) : (
                    <span className={`font-semibold text-base ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{cardName}</span>
                  )}
                  
                  {/* Verified badge for seller */}
                  {cardTitle === 'Seller' && (
                    <span className="bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded-full">
                      Verified
                    </span>
                  )}
                </div>
                
                {/* Online status */}
                <p className={`text-sm mb-1 ${cardOnline ? 'text-green-600' : (isDarkMode ? 'text-gray-400' : 'text-gray-400')}`}>
                  {cardOnline ? 'Online' : 'Offline'}
                </p>
                
                {/* Rating */}
                <div className={`flex items-center gap-1 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <polygon points="10,1 12,7 18,7 13,11 15,17 10,13 5,17 7,11 2,7 8,7" />
                  </svg>
                  <span>{(cardTitle === 'Seller' ? product.seller.rating : product.buyer.rating)}</span>
                  <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
                    ({(cardTitle === 'Seller' ? product.seller.reviews : product.buyer.reviews)} reviews)
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 